/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import { EventsList } from './models/events';
import Component from 'can/component/component';
import appContext from 'scripts/app-context';
import template from './streaming-schedule.stache';
import deviceUtil from 'scripts/utils/device';
import _ from 'lodash';
import can from 'can';
import dayjs from 'dayjs';
import urlUtil from 'scripts/utils/url';
import liveVideoService from 'scripts/services/live-video-service';
import FeedService from 'scripts/services/base-feed-service';
import liveVideoPlayer from './live-video-player.stache';

const EVENT_LIVE = 'live';
const EVENT_UPCOMING = 'upcoming';

const MAX_PERIOD_TO_SEARCH_CLOSEST = 100; //days
const streamingScheduleUrl = window.globalSettings.feeds.schedule.streamingUrl;
const inWebView = window.globalSettings.inWebView;

Component.extend({
    tag: 'streaming-schedule',
    template: template,
    viewModel: {
        playerUrlTemplate:
            _.get(globalSettings, 'pagePaths.player') ||
            'players/{playerId}.html',
        holesUrlTemplate:
            _.get(globalSettings, 'pagePaths.holes') || 'holes/{holeId}.html',
        schedule: {
            date: null,
            dayOfWeek: '',
            eventsLive: [],
            eventsUpcoming: [],
        },
        audioUrl: window.globalSettings.pagePaths.listen,
        define: {
            date: {
                set: function(value) {
                    return value ? dayjs(value).format() : dayjs().format();
                },
            },
            events: {
                Type: EventsList,
                Value: EventsList,
            },
        },
        getHoleNumber: function(id) {
            if (id) {
                return String(id).replace(/USOpen:holes\/*/gi, '') || null;
            }

            return null;
        },
        onVideoClick: function(video) {
            if (deviceUtil.isMobile && !inWebView) {
                this.LiveVideoPlayer = liveVideoPlayer({
                    liveVideo: { video: video },
                    closeLiveVideoPlayer: () => {
                        $('live-player').remove();
                    },
                });
                $('body').append(this.LiveVideoPlayer);
            } else {
                liveVideoService.openWindow(video.identifier);
            }
        },
    },
    helpers: {
        playerPageUrl: function(options) {
            return can.sub(
                this.attr('playerUrlTemplate'),
                options.context['identifier'],
            );
        },
        holesPageUrl: function(options) {
            return can.sub(
                this.attr('holesUrlTemplate'),
                options.context['holeNumber'],
            );
        },
        liveUrl: function(options) {
            return liveVideoService.getLiveUrl(
                options.context['video'].identifier,
            );
        },
    },
    events: {
        init: function() {
            new FeedService({ feedUrl: streamingScheduleUrl, fromCache: false })
                .on('data', (type, data) => {
                    if (!_.isEqual(this.previousData, data)) {
                        this.previousData = data;
                        this.viewModel.attr('events', data);
                        let schedule = this.getActualSchedule();
                        this.viewModel.attr('schedule', schedule);
                        appContext.model.dispatch(
                            'on-streaming-schedule-data',
                            [schedule],
                        );
                    }
                })
                .on('error', (error) => {
                    console.error(
                        'Error fetching streaming schedule data',
                        error,
                    );
                })
                .fetch();
        },

        isLiveEvent: function(event) {
            return String(event.state).toLowerCase() === EVENT_LIVE;
        },

        isUpcomingEvent: function(event) {
            return String(event.state).toLowerCase() === EVENT_UPCOMING;
        },

        getActualSchedule: function() {
            let events = [];
            let day = dayjs(this.viewModel.attr('date'));

            let closestUpcomingDiff = -1 * MAX_PERIOD_TO_SEARCH_CLOSEST;
            let closestDiff = -1 * MAX_PERIOD_TO_SEARCH_CLOSEST;
            let closestUpcomingDay = null;
            let closestDay = null;

            this.viewModel.attr('events').each((event) => {
                let startTime = event.startTime
                    ? dayjs(event.startTime)
                    : false;
                let endTime = event.endTime ? dayjs(event.endTime) : false;
                let isValidDates =
                    startTime &&
                    endTime &&
                    startTime.isValid() &&
                    endTime.isValid();
                let diff = isValidDates
                    ? dayjs(day.format('Y-MM-DD')).diff(
                          startTime.format('Y-MM-DD'),
                          'day',
                      )
                    : MAX_PERIOD_TO_SEARCH_CLOSEST;
                if (event.holes) {
                    event.holes = event.holes.map((hole) => {
                        hole.holeNumber = this.viewModel.getHoleNumber(
                            hole.identifier,
                        );

                        return hole.holeNumber ? hole : null;
                    });
                }

                if (!isValidDates) {
                    return;
                }

                // event in progress
                if (
                    startTime.isBefore(day, 'minutes') &&
                    endTime.isAfter(day, 'minutes')
                ) {
                    diff = 0;
                }

                if (
                    Math.abs(diff) < MAX_PERIOD_TO_SEARCH_CLOSEST &&
                    endTime.isAfter(day, 'minutes') &&
                    this.isUpcomingEvent(event)
                ) {
                    if (diff <= 0 && diff > closestDiff) {
                        closestDiff = diff;
                        closestDay = startTime;
                    }

                    if (diff <= 0 && diff > closestUpcomingDiff) {
                        closestUpcomingDiff = diff;
                        closestUpcomingDay = startTime;
                    }

                    events.push(
                        _.extend(event, {
                            timestamp: startTime.valueOf(),
                            diff: diff,
                        }),
                    );
                } else if (this.isLiveEvent(event)) {
                    if (diff <= 0 && diff > closestDiff) {
                        closestDiff = diff;
                        closestDay = startTime;
                    }
                    events.push(
                        _.extend(event, {
                            timestamp: startTime.valueOf(),
                            diff: 0,
                        }),
                    );
                }
            });

            let eventsGroups = _.groupBy(
                _.orderBy(events, ['timestamp', 'asc']),
                function(item) {
                    return String(item.state).toLowerCase() + ':' + item.diff;
                },
            );

            const [audio, video] = _.partition(
                eventsGroups[`${EVENT_LIVE}:0`],
                (it) => it.channelIdentifier === 'audio',
            );

            // Try Live and Upcoming for this day
            let schedule = {
                dayOfWeek:
                    closestDiff === 0
                        ? day.format('dddd')
                        : dayjs(closestDay ? closestDay : day).format('dddd'),
                eventsLive:
                    audio.length || video.length
                        ? {
                              audio,
                              video,
                          }
                        : null,
                eventsUpcoming: eventsGroups[`${EVENT_UPCOMING}:0`],
            };

            if (
                !schedule.eventsUpcoming &&
                !schedule.eventsLive &&
                eventsGroups[`${EVENT_UPCOMING}:${closestUpcomingDiff}`]
            ) {
                // Upcoming for closest upcoming day exist
                _.extend(schedule, {
                    eventsUpcoming:
                        eventsGroups[
                            `${EVENT_UPCOMING}:${closestUpcomingDiff}`
                        ],
                });
            }

            if (!schedule.eventsLive && !schedule.eventsUpcoming) {
                // no actual data
                schedule = null;
            }

            return schedule;
        },
    },
});
